<template>
  <v-container
    grid-list-xl
    fluid
  >
    <LoadingState
      :loading="loading"
      color="primary"
    />
    <v-layout
      v-if="!loading"
      row
      wrap
    >
      <v-flex
        xs12
      >
        <OrderSingleHeader :order="order" />
      </v-flex>

      <v-flex
        xs12
        md8
      >
        <OrderSingleSummary
          :order="order"
          @fulfill="prepareShipping"
        />
        <OrderSinglePaid
          :order="order"
          @refund="refundDialog = true"
          @mark-as-paid="markAsPaidDialog = true"
        />

        <OrderSingleTransactions
          :order="order"
          class="mb-4"
        />

        <VWidget
          :title="$t('Timeline Track')">
          <div slot="widget-content">
            <OrderSingleTimeline
              :order="order"
              @add-comment="comment"/>
          </div>
        </VWidget>
      </v-flex>

      <v-flex
        xs12
        md4
      >
        <VWidget
          v-if="order.holdReasons.length"
          title="Hold Reasons"
          class="mb-4"
        >
          <div slot="widget-content">
            <v-list
              dense
              three-line
            >
              <template v-for="item in order.holdReasons">
                <v-list-tile
                  :key="item.id"
                  avatar
                >
                  <v-list-tile-avatar>
                    <v-icon
                      v-if="item.resolved_at"
                      color="green"
                    >
                      check
                    </v-icon>
                    <v-icon
                      v-else
                      color="error"
                    >
                      close
                    </v-icon>
                  </v-list-tile-avatar>

                  <v-list-tile-content>
                    <v-list-tile-title>{{ item.reason }}</v-list-tile-title>
                    <v-list-tile-sub-title two-line>
                      <div>
                        {{ $t('Created At') }}: {{ item.created_at | timezone }}
                      </div>
                      <div v-if="item.resolved_at">
                        {{ $t('Resolved At') }}: {{ item.resolved_at | timezone }}
                      </div>
                    </v-list-tile-sub-title>
                  </v-list-tile-content>
                </v-list-tile>
              </template>
            </v-list>
          </div>
        </VWidget>
        <order-single-customer :order="order" />

        <order-single-note
          v-if="order.note"
          :note="order.note"
          class="mb-4"
        />

        <order-single-documents
          v-if="order.documents && order.documents.length > 0"
          :order="order"
          class="mb-4"
        />

        <order-i-count-documents
          :order="order"
          class="mb-4"
        />

        <order-single-applied-discounts
          v-if="hasDiscounts"
          :order="order"
          class="mb-4"
        />

        <order-single-created-by
          v-if="order.created_by"
          :created-by="order.created_by"
          class="mb-4"
        />
      </v-flex>
      <v-dialog
        v-model="shippingDialog"
        persistent
        max-width="800px"
      >
        <div>
          <VWidget
            :title="$t('Fulfill order')"
          >
            <v-layout
              slot="widget-content"
              row
              wrap
            >
              <v-flex xs12>
                <v-layout
                  row
                  wrap
                >
                  <v-flex
                    xs12
                  >
                    <v-layout
                      v-for="item in shipment.items"
                      :key="item.sku"
                      row
                      wrap
                    >
                      <v-flex
                        xs2
                        class="ma-2"
                      >
                        <v-img
                          :src="$helpers.imageFrom(item.image, 'tiny')"
                          aspect-ratio="1"
                          width="60"
                        />
                      </v-flex>
                      <v-flex
                        xs6
                        class="ma-2"
                      >
                        <div class="subheading info--text">
                          {{ item.name }}
                        </div>
                        <div class="text--secondary">
                          {{ $t('SKU:') }} {{ item.sku }}
                        </div>
                      </v-flex>
                      <v-flex
                        xs3
                        class="align-self-center"
                      >
                        <CountIncrement
                          v-model="item.quantity"
                          :min="0"
                          :max="item.max"
                          :step="1"
                        />
                      </v-flex>
                      <v-flex xs12>
                        <v-divider />
                      </v-flex>
                    </v-layout>

                    <v-layout
                      row
                      wrap
                    >
                      <v-flex
                        xs12
                        sm6
                      >
                        <v-text-field
                          v-model="shipment.carrier"
                          :error-messages="shipment.displayErrors('carrier')"
                          :label="$t('Carrier')"
                          outline
                          class="mx-1 mt-3"
                        />
                      </v-flex>
                      <v-flex
                        xs12
                        sm6
                      >
                        <v-text-field
                          v-model="shipment.tracking_number"
                          :error-messages="shipment.displayErrors('tracking_number')"
                          :label="$t('Tracking number')"
                          outline
                          class="mx-1 mt-3"
                        />
                      </v-flex>
                    </v-layout>
                  </v-flex>
                </v-layout>
              </v-flex>
            </v-layout>
          </VWidget>
          <v-card>
            <v-card-actions>
              <v-spacer />
              <v-btn
                :loading="shipment.inProgress"
                color="primary darken-1"
                small
                @click="fulfill"
              >
                {{ $t('Ship') }}
              </v-btn>
              <v-btn
                color="darken-1"
                flat
                small
                @click="shippingDialog = false"
              >
                {{ $t('Close') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </div>
      </v-dialog>
      <order-single-refund-dialog
        v-if="refundDialog"
        :order="order"
        :refund-dialog="refundDialog"
        @close-refund-dialog="refundDialog = false"
      />
      <order-single-mark-as-paid-dialog
        :order="order"
        :mark-as-paid-dialog="markAsPaidDialog"
        @close-mark-as-paid-dialog="markAsPaidDialog = false"
      />
    </v-layout>
  </v-container>
</template>

<script>
import OrderSingleHeader from './OrderSingleHeader'
import LoadingState from '@/components/LoadingState'
import OrderSinglePaid from './OrderSinglePaid'
import OrderSingleSummary from './OrderSingleSummary'
import OrderSingleTimeline from './OrderSingleTimeline'
import OrderSingleCustomer from './OrderSingleCustomer'
import OrderSingleRefundDialog from './OrderSingleRefundDialog'
import OrderSingleMarkAsPaidDialog from './OrderSingleMarkAsPaidDialog'
import CountIncrement from '@/components/CountIncrement'
import OrderSingleNote from './OrderSingleNote'
import OrderSingleCreatedBy from '@/modules/Orders/components/OrderSingle/OrderSingleCreatedBy'
import OrderSingleDocuments from '@/modules/Orders/components/OrderSingle/OrderSingleDocuments'
import OrderICountDocuments from '@/modules/Orders/components/OrderSingle/OrderICountDocuments'
import OrderSingleTransactions from '@/modules/Orders/components/OrderSingle/OrderSingleTransactions'
import OrderSingleAppliedDiscounts from './OrderSingleAppliedDiscounts'

export {
  OrderSingleCustomer,
  OrderSinglePaid,
  OrderSingleSummary,
  LoadingState,
  OrderSingleHeader,
  CountIncrement,
  OrderSingleTimeline
}

export default {
  name: 'OrderSingle',

  components: {
    OrderSingleAppliedDiscounts,
    OrderSingleTransactions,
    OrderICountDocuments,
    OrderSingleDocuments,
    OrderSingleCreatedBy,
    OrderSingleNote,
    OrderSingleCustomer,
    OrderSingleTimeline,
    OrderSinglePaid,
    OrderSingleSummary,
    LoadingState,
    OrderSingleHeader,
    OrderSingleRefundDialog,
    OrderSingleMarkAsPaidDialog,
    CountIncrement
  },

  props: {
    order: {
      type: Object,
      required: true
    }
  },

  data () {
    return {
      refundDialog: false,
      markAsPaidDialog: false,
      shippingDialog: false,
      shipment: this.$http.make('shipment')
    }
  },

  computed: {
    loading () {
      return Object.entries(this.order).length === 0
    },

    hasDiscounts () {
      const discounts = [...this.order.items, this.order.shipping].reduce(function (accumulator, item) {
        accumulator.push(...item.discounts)
        return accumulator
      }, [])

      return discounts.length > 0
    }
  },

  methods: {
    prepareShipping () {
      this.shipment = this.$http.make('shipment')
      this.order.unfulfilled.forEach(item => {
        this.shipment.items.push({
          item_id: item.id,
          quantity: item.quantity,
          max: item.quantity,
          sku: item.sku,
          image: item.image_url,
          name: item.name
        })
      })
      this.shippingDialog = true
    },

    async comment (msg) {
      this.order.addComment(msg)
    },

    async fulfill () {
      this.shipment.items = this.shipment.items.filter(item => item.quantity)
      try {
        await this.shipment
          .for(this.order)
          .save()

        this.shippingDialog = false
        this.order.shipments.push(this.shipment)
        this.$radio.$emit('item-saved', { message: `Added shipment` })
      } catch (e) {
        this.$vuetify.goTo(0)
        this.$radio.$emit('api-error', { message: 'Validation failed', errors: this.shipment.errors })
      }
    }
  }
}
</script>
